// Formulários do cadastro de colaboradores (pessoa, contato, vínculo), usados na Nova admissão e
// no detalhe do colaborador. O estado do formulário é sempre texto; as funções *ParaApi convertem
// para o payload da API (vazio → null, "sim"/"nao" → boolean, número em texto BR → "1234.56").
import { useState } from "react";
import { Search } from "lucide-react";
import { getRhConsultaCnpj } from "../../api";
import { Campo, NumeroInput, btnStyle, formatBRL, inputStyle, numeroParaInput, parseNumero } from "../estoque/ui";

const texto = (v) => (v === null || v === undefined ? "" : String(v));
const nulo = (v) => (v === "" || v === undefined ? null : v);
const boolParaForm = (v) => (v === true ? "sim" : v === false ? "nao" : "");
const formParaBool = (v) => (v === "sim" ? true : v === "nao" ? false : null);
const inteiro = (v) => (v === "" || v === null || v === undefined ? null : parseInt(v, 10));

export function mascaraCpf(v) {
  const d = String(v || "").replace(/\D/g, "").slice(0, 11);
  return d.replace(/^(\d{3})(\d)/, "$1.$2").replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3").replace(/\.(\d{3})(\d)/, ".$1-$2");
}

export function mascaraCnpj(v) {
  const d = String(v || "").replace(/\D/g, "").slice(0, 14);
  return d.replace(/^(\d{2})(\d)/, "$1.$2").replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1/$2").replace(/(\d{4})(\d)/, "$1-$2");
}

// ─── Conversões form ⇄ API ───────────────────────────────────────────────────

const CAMPOS_PESSOA_TEXTO = ["nome", "cpf", "rg", "orgao_expedidor_rg", "data_nascimento", "sexo", "genero", "orientacao_sexual",
  "raca", "estado_civil", "formacao", "area_formacao", "instituicao_ensino", "previsao_formacao"];
const CAMPOS_CONTATO = ["email_youx", "email_pessoal", "telefone", "endereco", "numero", "complemento", "bairro", "cidade", "uf", "cep", "contato_emergencia"];
const CAMPOS_CONTRATO_TEXTO = ["tipo_contratacao", "cargo", "nivel", "unidade", "codigo_colaborador", "status", "data_admissao",
  "data_desligamento", "motivo_desligamento", "tipo_desligamento", "modalidade_trabalho", "data_inicio_unidade", "data_inicio_clt",
  "grupo_salarial", "step", "centro_custo_beneficios"];
const CAMPOS_PJ = ["cnpj", "razao_social", "natureza_juridica", "endereco_sede", "email_contrato", "data_inicio_pj"];

export function pessoaParaForm(p = {}) {
  const f = {};
  CAMPOS_PESSOA_TEXTO.forEach((c) => { f[c] = texto(p[c]); });
  f.deficiencia_pcd = boolParaForm(p.deficiencia_pcd);
  f.possui_filhos = boolParaForm(p.possui_filhos);
  f.qtd_filhos = texto(p.qtd_filhos);
  return f;
}

export function pessoaParaApi(f) {
  const p = {};
  CAMPOS_PESSOA_TEXTO.forEach((c) => { p[c] = nulo(f[c].trim()); });
  p.deficiencia_pcd = formParaBool(f.deficiencia_pcd);
  p.possui_filhos = formParaBool(f.possui_filhos);
  p.qtd_filhos = inteiro(f.qtd_filhos);
  return p;
}

export function contatoParaForm(c) {
  const f = {};
  CAMPOS_CONTATO.forEach((k) => { f[k] = texto(c?.[k]); });
  return f;
}

export function contatoParaApi(f) {
  const c = {};
  CAMPOS_CONTATO.forEach((k) => { c[k] = nulo(f[k].trim()); });
  return c;
}

export function contratoParaForm(c = {}) {
  const f = {};
  CAMPOS_CONTRATO_TEXTO.forEach((k) => { f[k] = texto(c[k]); });
  if (!f.status) f.status = "Ativo";
  f.empresa_contratante_id = texto(c.empresa_contratante_id);
  f.carga_horaria_mensal = texto(c.carga_horaria_mensal);
  f.valor_prestacao_servico = numeroParaInput(c.valor_prestacao_servico);
  f.valor_nf = numeroParaInput(c.valor_nf);
  f.emite_declaracao_chronos = boolParaForm(c.emite_declaracao_chronos);
  f.beneficios = Object.fromEntries((c.beneficios || []).map((b) => [b.codigo, numeroParaInput(b.valor_mensal)]));
  f.pj = {};
  CAMPOS_PJ.forEach((k) => { f.pj[k] = texto(c.pj?.[k]); });
  f.pj.cnpj = texto(c.cnpj_prestador);
  return f;
}

export function contratoParaApi(f) {
  const c = {};
  CAMPOS_CONTRATO_TEXTO.forEach((k) => { c[k] = nulo(f[k].trim()); });
  c.empresa_contratante_id = inteiro(f.empresa_contratante_id);
  c.carga_horaria_mensal = inteiro(f.carga_horaria_mensal);
  c.valor_prestacao_servico = parseNumero(f.valor_prestacao_servico);
  c.valor_nf = parseNumero(f.valor_nf);
  c.emite_declaracao_chronos = formParaBool(f.emite_declaracao_chronos);
  c.beneficios = Object.entries(f.beneficios)
    .map(([codigo, valor]) => ({ codigo, valor_mensal: parseNumero(valor) }))
    .filter((b) => b.valor_mensal !== null && Number(b.valor_mensal) > 0);
  if (f.tipo_contratacao === "PJ") {
    c.pj = {};
    CAMPOS_PJ.forEach((k) => { c.pj[k] = nulo(f.pj[k].trim()); });
  }
  return c;
}

// ─── Blocos de formulário ────────────────────────────────────────────────────

export function Grade({ children, min = 200 }) {
  return <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fill, minmax(${min}px, 1fr))`, gap: 12 }}>{children}</div>;
}

export function Secao({ C, titulo, descricao, children, acoes }) {
  return (
    <section style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 16, marginBottom: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 15, fontWeight: 800 }}>{titulo}</h2>
          {descricao && <p style={{ margin: "2px 0 0", fontSize: 12, color: C.muted }}>{descricao}</p>}
        </div>
        {acoes}
      </div>
      {children}
    </section>
  );
}

function Entrada({ C, form, setForm, campo, label, obrigatorio, tipo = "text", lista, ...rest }) {
  return (
    <Campo C={C} label={label} obrigatorio={obrigatorio}>
      <input type={tipo} value={form[campo]} list={lista} onChange={(e) => setForm({ ...form, [campo]: e.target.value })} style={inputStyle(C)} {...rest} />
    </Campo>
  );
}

function Escolha({ C, form, setForm, campo, label, opcoes, obrigatorio, vazio = "—" }) {
  return (
    <Campo C={C} label={label} obrigatorio={obrigatorio}>
      <select value={form[campo]} onChange={(e) => setForm({ ...form, [campo]: e.target.value })} style={inputStyle(C)}>
        {vazio !== null && <option value="">{vazio}</option>}
        {opcoes.map((o) => (typeof o === "string"
          ? <option key={o} value={o}>{o}</option>
          : <option key={o.valor} value={o.valor}>{o.rotulo}</option>))}
      </select>
    </Campo>
  );
}

const SIM_NAO = [{ valor: "sim", rotulo: "Sim" }, { valor: "nao", rotulo: "Não" }];

export function FormPessoa({ C, form, setForm }) {
  const p = { C, form, setForm };
  return (
    <>
      <Grade>
        <Entrada {...p} campo="nome" label="Nome completo" obrigatorio />
        <Campo C={C} label="CPF" obrigatorio>
          <input value={form.cpf} inputMode="numeric" onChange={(e) => setForm({ ...form, cpf: mascaraCpf(e.target.value) })} style={inputStyle(C)} />
        </Campo>
        <Entrada {...p} campo="data_nascimento" label="Data de nascimento" tipo="date" />
        <Entrada {...p} campo="rg" label="RG" />
        <Entrada {...p} campo="orgao_expedidor_rg" label="Órgão expedidor" />
        <Entrada {...p} campo="sexo" label="Sexo" />
        <Entrada {...p} campo="estado_civil" label="Estado civil" />
        <Escolha {...p} campo="possui_filhos" label="Possui filhos" opcoes={SIM_NAO} />
        <Entrada {...p} campo="qtd_filhos" label="Quantidade de filhos" tipo="number" min="0" />
      </Grade>

      <div style={{ fontSize: 12, fontWeight: 800, color: C.muted, margin: "16px 0 8px" }}>FORMAÇÃO</div>
      <Grade>
        <Entrada {...p} campo="formacao" label="Formação" />
        <Entrada {...p} campo="area_formacao" label="Área de formação" />
        <Entrada {...p} campo="instituicao_ensino" label="Instituição de ensino" />
        <Entrada {...p} campo="previsao_formacao" label="Previsão de formação" placeholder="ex.: 12/2027" />
      </Grade>

      <div style={{ fontSize: 12, fontWeight: 800, color: C.muted, margin: "16px 0 4px" }}>DADOS SENSÍVEIS (LGPD)</div>
      <p style={{ margin: "0 0 8px", fontSize: 11, color: C.muted }}>Autodeclarados e opcionais. As alterações ficam registradas sem o valor.</p>
      <Grade>
        <Entrada {...p} campo="genero" label="Gênero" />
        <Entrada {...p} campo="orientacao_sexual" label="Orientação sexual" />
        <Entrada {...p} campo="raca" label="Raça/cor" />
        <Escolha {...p} campo="deficiencia_pcd" label="Pessoa com deficiência" opcoes={SIM_NAO} />
      </Grade>
    </>
  );
}

export function FormContato({ C, form, setForm }) {
  const p = { C, form, setForm };
  return (
    <Grade>
      <Entrada {...p} campo="email_youx" label="E-mail YOUX" tipo="email" />
      <Entrada {...p} campo="email_pessoal" label="E-mail pessoal" tipo="email" />
      <Entrada {...p} campo="telefone" label="Telefone" />
      <Entrada {...p} campo="contato_emergencia" label="Contato de emergência" />
      <Entrada {...p} campo="cep" label="CEP" />
      <Entrada {...p} campo="endereco" label="Endereço" />
      <Entrada {...p} campo="numero" label="Número" />
      <Entrada {...p} campo="complemento" label="Complemento" />
      <Entrada {...p} campo="bairro" label="Bairro" />
      <Entrada {...p} campo="cidade" label="Cidade" />
      <Entrada {...p} campo="uf" label="UF" maxLength={2} />
    </Grade>
  );
}

function DadosPj({ C, form, setForm }) {
  const pj = form.pj;
  const setPj = (dados) => setForm({ ...form, pj: { ...pj, ...dados } });
  const [consultando, setConsultando] = useState(false);
  const [aviso, setAviso] = useState("");

  async function consultar() {
    setConsultando(true);
    setAviso("");
    try {
      const r = await getRhConsultaCnpj(pj.cnpj);
      setPj({ cnpj: r.cnpj, razao_social: r.razao_social, natureza_juridica: r.natureza_juridica, endereco_sede: r.endereco_sede || pj.endereco_sede });
      if (!r.endereco_sede) setAviso("A Receita não tem o endereço deste CNPJ — preencha manualmente.");
    } catch (e) {
      setAviso(e.message);
    } finally {
      setConsultando(false);
    }
  }

  return (
    <div style={{ border: `1px dashed ${C.border}`, borderRadius: 10, padding: 12, marginTop: 16 }}>
      <div style={{ fontSize: 12, fontWeight: 800, color: C.muted, marginBottom: 8 }}>PESSOA JURÍDICA</div>
      <Grade>
        <Campo C={C} label="CNPJ" obrigatorio>
          <div style={{ display: "flex", gap: 6 }}>
            <input value={pj.cnpj} inputMode="numeric" onChange={(e) => setPj({ cnpj: mascaraCnpj(e.target.value) })} style={inputStyle(C)} />
            <button type="button" onClick={consultar} disabled={consultando || pj.cnpj.replace(/\D/g, "").length !== 14}
              title="Buscar razão social, natureza e endereço na Receita" style={{ ...btnStyle(C.brand, true), padding: "0 12px", opacity: consultando ? 0.6 : 1 }}>
              <Search size={15} />
            </button>
          </div>
        </Campo>
        <Campo C={C} label="Razão social"><input value={pj.razao_social} onChange={(e) => setPj({ razao_social: e.target.value })} style={inputStyle(C)} /></Campo>
        <Campo C={C} label="Natureza jurídica"><input value={pj.natureza_juridica} onChange={(e) => setPj({ natureza_juridica: e.target.value })} style={inputStyle(C)} /></Campo>
        <Campo C={C} label="E-mail do contrato"><input type="email" value={pj.email_contrato} onChange={(e) => setPj({ email_contrato: e.target.value })} style={inputStyle(C)} /></Campo>
        <Campo C={C} label="Início como PJ" ajuda="Vigência inicial usada nos aditivos"><input type="date" value={pj.data_inicio_pj} onChange={(e) => setPj({ data_inicio_pj: e.target.value })} style={inputStyle(C)} /></Campo>
      </Grade>
      <div style={{ marginTop: 12 }}>
        <Campo C={C} label="Endereço da sede"><input value={pj.endereco_sede} onChange={(e) => setPj({ endereco_sede: e.target.value })} style={inputStyle(C)} /></Campo>
      </div>
      {aviso && <div style={{ fontSize: 12, color: C.warning, marginTop: 8 }}>{aviso}</div>}
    </div>
  );
}

export function FormContrato({ C, form, setForm, opcoes }) {
  const p = { C, form, setForm };
  const sugestoes = (id, itens) => <datalist id={id}>{itens.map((i) => <option key={i} value={i} />)}</datalist>;
  const totalBeneficios = Object.values(form.beneficios).reduce((s, v) => s + Number(parseNumero(v) || 0), 0);

  return (
    <>
      {sugestoes("rh-unidades", opcoes.unidades)}
      {sugestoes("rh-cargos", opcoes.cargos)}
      {sugestoes("rh-niveis", opcoes.niveis)}
      {sugestoes("rh-grupos", opcoes.grupos_salariais)}
      {sugestoes("rh-steps", opcoes.steps)}
      <Grade>
        <Escolha {...p} campo="tipo_contratacao" label="Tipo de contratação" opcoes={opcoes.tipos_contratacao} obrigatorio vazio="Selecione" />
        <Escolha {...p} campo="empresa_contratante_id" label="Empresa contratante" vazio="—"
          opcoes={opcoes.empresas.map((e) => ({ valor: String(e.id), rotulo: e.codigo }))} />
        <Entrada {...p} campo="data_admissao" label="Data de admissão" tipo="date" obrigatorio />
        <Escolha {...p} campo="status" label="Status" opcoes={opcoes.status} obrigatorio vazio={null} />
        <Entrada {...p} campo="unidade" label="Departamento" lista="rh-unidades" />
        <Entrada {...p} campo="cargo" label="Cargo" lista="rh-cargos" />
        <Entrada {...p} campo="nivel" label="Nível" lista="rh-niveis" />
        <Escolha {...p} campo="modalidade_trabalho" label="Modalidade" opcoes={opcoes.modalidades} />
        <Entrada {...p} campo="grupo_salarial" label="Grupo" lista="rh-grupos" />
        <Entrada {...p} campo="step" label="Step" lista="rh-steps" />
        <Entrada {...p} campo="carga_horaria_mensal" label="Carga horária mensal (h)" tipo="number" min="0" />
        <Campo C={C} label="Prestação de serviço / salário (R$)">
          <NumeroInput C={C} casas={2} value={form.valor_prestacao_servico} onChange={(v) => setForm({ ...form, valor_prestacao_servico: v })} />
        </Campo>
        <Campo C={C} label="Valor de NF (R$)">
          <NumeroInput C={C} casas={2} value={form.valor_nf} onChange={(v) => setForm({ ...form, valor_nf: v })} />
        </Campo>
        <Entrada {...p} campo="data_inicio_unidade" label="Início na unidade" tipo="date" />
        {form.tipo_contratacao === "CLT" && <Entrada {...p} campo="data_inicio_clt" label="Início como CLT" tipo="date" />}
        <Entrada {...p} campo="codigo_colaborador" label="Código do colaborador" />
        <Escolha {...p} campo="emite_declaracao_chronos" label="Chronos: emite declaração" opcoes={SIM_NAO} />
      </Grade>

      {form.tipo_contratacao === "PJ" && <DadosPj C={C} form={form} setForm={setForm} />}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", margin: "16px 0 8px" }}>
        <span style={{ fontSize: 12, fontWeight: 800, color: C.muted }}>BENEFÍCIOS (VALOR MENSAL)</span>
        <span style={{ fontSize: 12, color: C.muted }}>Total {formatBRL(totalBeneficios)}</span>
      </div>
      <Grade min={170}>
        {opcoes.beneficios.map((b) => (
          <Campo key={b.codigo} C={C} label={b.nome}>
            <NumeroInput C={C} casas={2} value={form.beneficios[b.codigo] || ""}
              onChange={(v) => setForm({ ...form, beneficios: { ...form.beneficios, [b.codigo]: v } })} />
          </Campo>
        ))}
        <Entrada {...p} campo="centro_custo_beneficios" label="Centro de custo dos benefícios" />
      </Grade>

      <div style={{ fontSize: 12, fontWeight: 800, color: C.muted, margin: "16px 0 8px" }}>DESLIGAMENTO</div>
      <Grade>
        <Entrada {...p} campo="data_desligamento" label="Data do desligamento" tipo="date" />
        <Escolha {...p} campo="tipo_desligamento" label="Tipo" opcoes={opcoes.tipos_desligamento} />
        <Entrada {...p} campo="motivo_desligamento" label="Motivo" />
      </Grade>
    </>
  );
}
